package com.exura.features.localization

import com.exura.features.localization.domain.model.BasicLangSet
import com.exura.features.localization.domain.model.Language
import com.exura.features.localization.domain.model.Localization


interface CommonLang {
    val controls: Controls
    val errors: Errors
    val nav: Navigation
    val crud: CRUD

    interface Controls {
        val cancel: String
        val backToDashboard: String
        val refresh: String

        val yes: String
        val no: String
        val areYouSure: String

        val confirmChanges: String
        val reject: String

        val clickToEnlarge: String
        val openInNewTab: String
        val copyLink: String
        val copyLinkCopied: String

        val addImage: String
        val uploadFromComputer: String
        val changeFile: String
    }

    interface CRUD {
        val create: String
        val replace: String
        val update: String
        val delete: String

        val save: String
        val edit: String
        val available: String
        val notFound: String
        val preview: String

        val send: String
    }

    interface Errors {
        val unknownErrorOccurred: String
    }

    interface Navigation {
        val lightMode: String
        val darkMode: String
        val account: String

        val library: String
        val templates: String
        val createTemplate: String
        val publicTemplate: String
        val importTemplate: String

        val settings: String
        val profile: String
        val logout: String

        val confirmLogout: String
    }
}

data object CommonPolish : CommonLang {
    override val controls = Controls
    override val errors = Errors
    override val nav = Navigation
    override val crud = CRUD

    data object Controls : CommonLang.Controls {
        override val cancel = "Anuluj"
        override val backToDashboard = "Powrót do panelu"
        override val refresh: String = "Odśwież"

        override val yes = "Tak"
        override val no = "Nie"
        override val areYouSure: String = "Na pewno?"

        override val confirmChanges: String = "Zatwierdź zmiany"
        override val reject: String = "Odrzuć"

        override val clickToEnlarge: String = "(kliknij, aby powiększyć)"
        override val openInNewTab: String = "Otwórz w nowej karcie"
        override val copyLink: String = "Skopiuj link"
        override val copyLinkCopied: String = "Skopiowano!"
        override val addImage: String = "Dodaj grafikę"
        override val uploadFromComputer: String = "Wybierz z dysku"
        override val changeFile: String = "Zamień plik"
    }

    data object CRUD : CommonLang.CRUD {
        override val create: String = "Stwórz"
        override val replace: String = "Zamień"
        override val update: String = "Aktualizuj"
        override val delete: String = "Usuń"

        override val save: String = "Zapisz"
        override val edit: String = "Edutuj"
        override val preview: String = "Podgląd"
        override val notFound: String = "Nie znaleziono"
        override val available: String = "Dostępne"
        override val send: String = "Prześlij"
    }

    data object Errors : CommonLang.Errors {
        override val unknownErrorOccurred = "Wystąpił nieznany błąd"
    }

    data object Navigation: CommonLang.Navigation {

        override val lightMode: String = "Tryb jasny"
        override val darkMode: String = "Tryb ciemny"
        override val account: String = "Konto"

        override val library: String = "Biblioteka"
        override val templates: String = "Szablony leczenia"
        override val createTemplate: String = "Stwórz"
        override val publicTemplate: String = "Plany publiczne"
        override val importTemplate: String = "Importuj"

        override val settings: String = "Ustawienia"
        override val profile: String = "Profil"
        override val logout: String = "Wyloguj"

        override val confirmLogout: String = "Czy chcesz się wylogować?"
    }
}

data object CommonEnglish : CommonLang {
    override val controls = Controls
    override val errors = Errors
    override val nav = Navigation
    override val crud = CRUD

    data object Controls : CommonLang.Controls {
        override val cancel: String = "Cancel"
        override val backToDashboard = "Back to dashboard"
        override val refresh: String = "Refresh"
        override val yes: String = "Yes"
        override val no: String = "No"
        override val areYouSure: String = "Are you sure?"

        override val confirmChanges: String = "Confirm changes"
        override val reject: String = "Reject"

        override val clickToEnlarge: String = "(click to open)"
        override val openInNewTab: String = "Open in new tab"
        override val copyLink: String = "Copy link"
        override val copyLinkCopied: String = "Link copied!"

        override val addImage: String = "Add image"
        override val uploadFromComputer: String = "Upload from computer"
        override val changeFile: String = "Change file"
    }

    data object CRUD : CommonLang.CRUD {
        override val create: String = "Create"
        override val replace: String = "Replace"
        override val update: String = "Update"
        override val delete: String = "Delete"

        override val save: String = "Save"
        override val edit: String = "Edit"
        override val preview: String = "Preview"
        override val notFound: String = "Not found"
        override val available: String = "Available"
        override val send: String = "Send"
    }

    data object Errors : CommonLang.Errors {
        override val unknownErrorOccurred: String = "Unknown Error Occurred"
    }

    data object Navigation: CommonLang.Navigation {

        override val lightMode: String = "Light mode"
        override val darkMode: String = "Dark mode"
        override val account: String = "Account"

        override val library: String = "Library"
        override val templates: String = "Templates"
        override val createTemplate: String = "Create"
        override val publicTemplate: String = "Public Plans"
        override val importTemplate: String = "Import"

        override val settings: String = "Settings"
        override val profile: String = "Profile"
        override val logout: String = "Logout"

        override val confirmLogout: String = "Are you sure to logout?"
    }
}

private val commonLangSet = BasicLangSet<CommonLang>(
    mapOf(
        Language.POLISH to CommonPolish,
        Language.ENGLISH to CommonEnglish,
    )
)

val commonLocalization = Localization<CommonLang>(commonLangSet)

/*
USAGE:
import com.exura.features.localization.commonLocalization

fun someFunction() {
    println(commonLocalization.current.controls.cancel)
}
 */
